import React, { useContext, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { useIntl } from "react-intl";
import { Formik, Field, Form } from "formik";
import { useApolloClient, gql } from "@apollo/client";
import * as Sentry from "@sentry/react";
import Swal from "sweetalert2";
import * as Yup from "yup";
import classnames from "classnames";

import getEncodedImageUrl from "../helpers/get-encoded-image";
import { Input, ProfileImage } from "../helpers/formik";
import UserContext from "../contexts/user";

const EditProfileModal: React.FC<{
  openProfileModal: boolean;
  setOpenProfileModal: (arg0: boolean) => void;
}> = ({ setOpenProfileModal, openProfileModal }) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState("1");
  const apolloClient = useApolloClient();
  const userContext = useContext(UserContext);
  const toggle = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  console.log("EditProfileModal",userContext.user)

  return (
    <Modal
      zIndex={9999}
      wrapClassName="f-modal-bd"
      isOpen={openProfileModal}
      toggle={() => setOpenProfileModal(!openProfileModal)}
      size="md"
      className="profile-modal"
    >
      <ModalHeader toggle={() => setOpenProfileModal(!openProfileModal)}>
        {intl.formatMessage({ id: "edit.profile" })}
      </ModalHeader>
      <ModalBody>
        <Nav tabs className="custom-tabs">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "1" })}
              onClick={() => {
                toggle("1");
              }}
            >

              {intl.formatMessage({ id: "auth.change.photo" })}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "2" })}
              onClick={() => {
                toggle("2");
              }}
            >
              {intl.formatMessage({ id: "auth.change.password" })}
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <br />
            <Formik
              initialValues={{
                name: userContext.user?.name,
                picture: userContext.user?.picture === null ? userContext.user.photo :  userContext.user?.picture,
                email: userContext.user?.email,
              }}
              onSubmit={(values) => {
                try {
                  apolloClient.mutate({
                    mutation: gql`
                      mutation ($id: uuid!, $picture: String, $name: String) {
                        update_user_by_pk(
                          pk_columns: { id: $id }
                          _set: { picture: $picture, name: $name }
                        ) {
                          id
                        }
                      }
                    `,
                    variables: {
                      id: userContext.user?.id,
                      picture: values.picture,
                      name: values.name,
                    },
                  });

                  Swal.fire({
                    title: intl.formatMessage({
                      id: "profile.update.success",
                    }),
                    icon: "success",
                  });
                } catch (e) {
                  ////Sentry.captureException(e);
                  Swal.fire({
                    title: intl.formatMessage({
                      id: "photo.update.error",
                    }),
                    icon: "error",
                  });
                }
              }}
            >
              {({ isSubmitting }) => {
                return (
                  <Form>
                    <Field
                      name="picture"
                      component={ProfileImage}
                      required
                      onChange={(val: string) => {
                        if (val) {
                          try {
                            apolloClient.mutate({
                              mutation: gql`
                                mutation ($id: uuid!, $picture: String) {
                                  update_user_by_pk(
                                    pk_columns: { id: $id }
                                    _set: { picture: $picture }
                                  ) {
                                    id
                                  }
                                }
                              `,
                              variables: {
                                id: userContext.user?.id,
                                picture: val,
                              },
                            });

                            Swal.fire({
                              title: intl.formatMessage({
                                id: "photo.update.success",
                              }),
                              icon: "success",
                            });
                          } catch (e) {
                            //Sentry.captureException(e);
                            Swal.fire({
                              title: intl.formatMessage({
                                id: "photo.update.error",
                              }),
                              icon: "error",
                            });
                          }
                        }
                      }}
                    />
                    <Field
                      name="name"
                      type="text"
                      component={Input}
                      placeholder={intl.formatMessage({ id: "input.name" })}
                      required
                    />
                    <Field
                      name="email"
                      type="email"
                      component={Input}
                      placeholder={intl.formatMessage({ id: "input.email" })}
                      disabled
                    />
                    <div className="text-center">
                        <Button
                          disabled={isSubmitting}
                          color="primary"
                          type="submit"
                        >
                          {intl.formatMessage({ id: "btn.submit" })}
                        </Button>
                      </div>
                    {
                      // values.profileImage && <Autosubmit values={values} submitForm={submitForm}  />
                    }
                  </Form>
                );
              }}
            </Formik>
          </TabPane>
          <TabPane tabId="2">
            <div className="c-password-form">
              <Formik
                validationSchema={Yup.object().shape({
                  newPassword: Yup.string()
                    .min(6, intl.formatMessage({ id: "form.too.short" }))
                    .required(intl.formatMessage({ id: "required" })),
                  confirmPassword: Yup.string().required(
                    intl.formatMessage({ id: "required" })
                  ),
                })}
                initialValues={{
                  newPassword: "",
                  confirmPassword: "",
                }}
                onSubmit={async (values, { resetForm }) => {
                  if (values.confirmPassword !== values.newPassword) {
                    Swal.fire({
                      title: intl.formatMessage({ id: "password.dont-match" }),
                      icon: "error",
                    });
                    return;
                  }
                  try {
                    await apolloClient.mutate({
                      mutation: gql`
                        mutation ($id: uuid!, $password: String) {
                          update_user_by_pk(
                            pk_columns: { id: $id }
                            _set: { password: $password }
                          ) {
                            id
                          }
                        }
                      `,
                      variables: {
                        id: userContext.user?.id,
                        password: values.newPassword,
                      },
                    });

                    resetForm();
                    Swal.fire({
                      title: intl.formatMessage({
                        id: "password.update.success",
                      }),
                      icon: "success",
                    });
                  } catch (e) {
                    //Sentry.captureException(e);
                    Swal.fire({
                      title: intl.formatMessage({
                        id: "password.update.error",
                      }),
                      icon: "error",
                    });
                  }
                }}
              >
                {({ isSubmitting }) => {
                  return (
                    <Form>
                      {/* <h3 className="m-t-20 m-b-20">{intl.formatMessage({ id: 'change.password' })}</h3> */}
                      <Field
                        name="newPassword"
                        type="password"
                        component={Input}
                        placeholder={intl.formatMessage({
                          id: "input.newPassword",
                        })}
                      />
                      <Field
                        name="confirmPassword"
                        type="password"
                        component={Input}
                        placeholder={intl.formatMessage({
                          id: "input.confirmPassword",
                        })}
                      />
                      <div className="text-center">
                        <Button
                          disabled={isSubmitting}
                          color="primary"
                          type="submit"
                        >
                          {intl.formatMessage({ id: "form.update.password" })}
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

export default EditProfileModal;
